import React, { useState, useEffect } from 'react';
import {
  ChevronDown, Search, Users, ShieldCheck, CreditCard, Hash,
  RefreshCw, X, AlertTriangle, UserCheck, UserX,
  CheckCircle, XCircle, PauseCircle, Ban, AlertOctagon
} from 'lucide-react';
import { superAdminAPI } from "../../services/api";
import { toast } from "react-toastify";

// ── All 6 ActionStatus options ────────────────────────────────────────────────
const ACTION_STATUSES = [
  { value: 'ACTIVATED',   label: 'Activated',   icon: CheckCircle,   badgeCls: 'bg-green-100 text-green-700',   btnCls: 'bg-green-600 hover:bg-green-700',   selCls: 'border-green-500 bg-green-50 text-green-700' },
  { value: 'DEACTIVATED', label: 'Deactivated', icon: XCircle,       badgeCls: 'bg-red-100 text-red-700',       btnCls: 'bg-red-500 hover:bg-red-600',       selCls: 'border-red-500 bg-red-50 text-red-700' },
  { value: 'ON_HOLD',     label: 'On Hold',     icon: PauseCircle,   badgeCls: 'bg-orange-100 text-orange-700', btnCls: 'bg-orange-500 hover:bg-orange-600', selCls: 'border-orange-500 bg-orange-50 text-orange-700' },
  { value: 'SUSPENDED',   label: 'Suspended',   icon: AlertOctagon,  badgeCls: 'bg-amber-100 text-amber-700',   btnCls: 'bg-amber-600 hover:bg-amber-700',   selCls: 'border-amber-500 bg-amber-50 text-amber-700' },
  { value: 'BANNED',      label: 'Banned',      icon: Ban,           badgeCls: 'bg-gray-200 text-gray-700',     btnCls: 'bg-gray-700 hover:bg-gray-800',     selCls: 'border-gray-500 bg-gray-100 text-gray-700' },
  { value: 'PENDING',     label: 'Pending',     icon: AlertTriangle, badgeCls: 'bg-yellow-100 text-yellow-700', btnCls: 'bg-yellow-500 hover:bg-yellow-600', selCls: 'border-yellow-500 bg-yellow-50 text-yellow-700' },
];

const getStatusCfg = (status) =>
  ACTION_STATUSES.find(s => s.value === status?.toUpperCase()) ?? {
    label: status || 'Unknown',
    badgeCls: 'bg-gray-100 text-gray-500',
    icon: AlertTriangle,
  };

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon size={22} className="text-white" />
    </div>
    <div>
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value ?? '—'}</p>
    </div>
  </div>
);

// ── Status Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = getStatusCfg(status);
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badgeCls}`}>
      {cfg.label}
    </span>
  );
};

// ── Payment Badge ─────────────────────────────────────────────────────────────
const PaymentBadge = ({ status }) => {
  const map = {
    PAID:      'bg-emerald-100 text-emerald-700',
    UNPAID:    'bg-rose-100 text-rose-700',
    FREETRIAL: 'style={{ backgroundColor: "#e8f4f9" }} style={{ color: "#1C4E6D" }}',
  };
  const cls = map[status?.toUpperCase()] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {status || '—'}
    </span>
  );
};

// ── Update Status Modal (same style as payment modal) ─────────────────────────
const UpdateStatusModal = ({ user, onClose, onSave }) => {
  const [selected, setSelected] = useState(user.actionStatus || 'PENDING');
  const selectedCfg = getStatusCfg(selected);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-indigo-600" />
            <h3 className="font-semibold text-gray-800">Update Account Status</h3>
          </div>
          <button onClick={onClose}>
            <X size={18} className="text-gray-400 hover:text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Updating status for{' '}
            <span className="font-semibold text-gray-800">
              {user.organizationName || user.email}
            </span>
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Account Status</label>
            <div className="grid grid-cols-2 gap-2">
              {ACTION_STATUSES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setSelected(value)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    selected === value
                      ? getStatusCfg(value).selCls + ' border-2'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Current → New preview */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-gray-400">Current:</span>
            <StatusBadge status={user.actionStatus} />
            <span className="text-gray-300">→</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${selectedCfg.badgeCls}`}>
              {selectedCfg.label}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(selected)}
            className={`flex-1 py-2 text-white rounded-lg text-sm font-semibold transition-colors ${selectedCfg.btnCls}`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Update Payment Modal ──────────────────────────────────────────────────────
const UpdatePaymentModal = ({ user, onClose, onSave }) => {
  const [paymentStatus, setPaymentStatus] = useState(user.paymentStatus || 'UNPAID');
  const options = [
    { value: 'PAID',      label: 'Paid' },
    { value: 'UNPAID',    label: 'Unpaid' },
    { value: 'FREETRIAL', label: 'Free Trial' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-indigo-600" />
            <h3 className="font-semibold text-gray-800">Update Payment Status</h3>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Updating payment for{' '}
            <span className="font-semibold text-gray-800">{user.organizationName || user.email}</span>
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Payment Status</label>
            <div className="grid grid-cols-3 gap-2">
              {options.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => setPaymentStatus(value)}
                  className={`py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    paymentStatus === value
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={() => onSave(paymentStatus)} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700">Save</button>
        </div>
      </div>
    </div>
  );
};

// ── Update User Count Modal ───────────────────────────────────────────────────
const UpdateUserCountModal = ({ user, onClose, onSave }) => {
  const [count, setCount] = useState(user.noOfUsers || 5);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center gap-2">
            <Hash size={18} className="text-purple-600" />
            <h3 className="font-semibold text-gray-800">Update User Count</h3>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Setting allowed users for{' '}
            <span className="font-semibold text-gray-800">{user.organizationName || user.email}</span>
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Number of Users</label>
            <input
              type="number" min="1" max="10000" value={count}
              onChange={e => setCount(parseInt(e.target.value) || 1)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-lg font-semibold text-center focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            />
            <div className="flex gap-2 mt-3">
              {[5, 10, 25, 50, 100].map(n => (
                <button key={n} onClick={() => setCount(n)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                    count === n ? 'bg-purple-600 text-white border-purple-600' : 'border-gray-200 text-gray-500 hover:border-purple-300'
                  }`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={() => onSave(count)} className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700">Save</button>
        </div>
      </div>
    </div>
  );
};

// ── Main SuperAdmin Component ─────────────────────────────────────────────────
const SuperAdmin = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [orgFilter, setOrgFilter] = useState('ALL');

  const [statusModal, setStatusModal] = useState(null);   // user object
  const [paymentModal, setPaymentModal] = useState(null); // user object
  const [userCountModal, setUserCountModal] = useState(null); // user object

  const stats = {
    total:    users.length,
    active:   users.filter(u => u.actionStatus?.toUpperCase() === 'ACTIVATED').length,
    inactive: users.filter(u => ['DEACTIVATED', 'SUSPENDED', 'BANNED', 'ON_HOLD'].includes(u.actionStatus?.toUpperCase())).length,
    paid:     users.filter(u => u.paymentStatus?.toUpperCase() === 'PAID').length,
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await superAdminAPI.getAllUsers();
      const data = Array.isArray(res.data) ? res.data : res.data.content || [];
      setUsers(data);
      setFilteredUsers(data);
    } catch {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    let filtered = [...users];
    if (search) {
      const q = search.toLowerCase();
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(q) ||
        u.organizationName?.toLowerCase().includes(q) ||
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(u => u.actionStatus?.toUpperCase() === statusFilter);
    }
    if (orgFilter !== 'ALL') {
      filtered = filtered.filter(u => u.organizationType === orgFilter);
    }
    setFilteredUsers(filtered);
  }, [search, statusFilter, orgFilter, users]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleUpdateStatus = async (userId, status) => {
    try {
      await superAdminAPI.updateUserStatus(userId, status);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, actionStatus: status } : u));
      const cfg = getStatusCfg(status);
      toast.success(`Status updated to ${cfg.label} ✅`);
    } catch {
      toast.error('Failed to update status ❌');
    } finally {
      setStatusModal(null);
    }
  };

  const handleUpdatePayment = async (userId, paymentStatus) => {
    try {
      await superAdminAPI.updatePaymentStatus(userId, paymentStatus);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, paymentStatus } : u));
      toast.success('Payment status updated ✅');
    } catch {
      toast.error('Failed to update payment status ❌');
    } finally {
      setPaymentModal(null);
    }
  };

  const handleUpdateUserCount = async (userId, count) => {
    try {
      await superAdminAPI.updateUserCount(userId, count);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, noOfUsers: count } : u));
      toast.success('User count updated ✅');
    } catch {
      toast.error('Failed to update user count ❌');
    } finally {
      setUserCountModal(null);
    }
  };

  // Wave-2 (CR-137): manual SUPERADMIN-only activation-email resend — never
  // mutates actionStatus; the button only appears for ACTIVATED accounts.
  // One canonical failure copy covers both a sent:false provider outcome and a
  // request/network failure — no raw backend/provider exception text, no
  // duplicate failure-message pattern.
  const handleResendActivationEmail = async (userId) => {
    try {
      const res = await superAdminAPI.resendActivationEmail(userId);
      if (res.data?.sent) {
        toast.success('Activation email sent.');
      } else {
        toast.error('Activation email could not be sent. Please try again.');
      }
    } catch {
      toast.error('Activation email could not be sent. Please try again.');
    }
  };

  const orgTypes = ['ALL', ...new Set(users.map(u => u.organizationType).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 bg-white border-b gap-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
            <ShieldCheck size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              Super Admin <ChevronDown size={18} className="text-gray-400" />
            </h1>
            <p className="text-xs text-gray-500">User &amp; Organization Management</p>
          </div>
          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full ml-1">
            {filteredUsers.length} records
          </span>
          {loading && <span className="text-xs text-indigo-600 animate-pulse">Loading...</span>}
        </div>
        <button
          onClick={fetchUsers}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4 sm:p-6">
        <StatCard icon={Users}      label="Total Users" value={stats.total}    color="bg-indigo-500" />
        <StatCard icon={UserCheck}  label="Active"       value={stats.active}   color="bg-green-500" />
        <StatCard icon={UserX}      label="Restricted"   value={stats.inactive} color="bg-red-400" />
        <StatCard icon={CreditCard} label="Paid"         value={stats.paid}     color="bg-emerald-500" />
      </div>

      {/* ── Filters ── */}
      <div className="mx-4 sm:mx-6 mb-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="text" placeholder="Search by email, org name…"
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 w-full border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
            />
          </div>
          <select
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-indigo-400 bg-white"
          >
            <option value="ALL">All Statuses</option>
            {ACTION_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <select
            value={orgFilter} onChange={e => setOrgFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-indigo-400 bg-white"
          >
            {orgTypes.map(t => <option key={t} value={t}>{t === 'ALL' ? 'All Org Types' : t}</option>)}
          </select>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="mx-4 sm:mx-6 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['#', 'Organization', 'Email', 'Org Type', 'Plan / Payment', 'Account Status', 'Email Verified', 'User Limit', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">
                    {loading ? 'Loading users…' : 'No users found'}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, idx) => (
                  <tr key={user.id || idx} className="hover:bg-indigo-50/30 transition-colors">

                    <td className="px-4 py-3 text-xs text-gray-400">{idx + 1}</td>

                    {/* Organization */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {(user.organizationName || user.firstName || user.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                            {user.organizationName || '—'}
                          </p>
                          <p className="text-xs text-gray-400">{user.firstName} {user.lastName}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{user.email || '—'}</td>

                    <td className="px-4 py-3">
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 whitespace-nowrap">
                        {user.organizationType || '—'}
                      </span>
                    </td>

                    <td className="px-4 py-3"><PaymentBadge status={user.paymentStatus} /></td>

                    <td className="px-4 py-3"><StatusBadge status={user.actionStatus} /></td>

                    {/* Email Verified — true=✓, false=✗, null/legacy=— (never render legacy null as ✗) */}
                    <td className="px-4 py-3 text-center text-sm font-semibold text-gray-600">
                      {user.emailVerified === true ? '✓' : user.emailVerified === false ? '✗' : '—'}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-gray-700">{user.noOfUsers ?? '—'}</span>
                        <span className="text-xs text-gray-400">users</span>
                      </div>
                    </td>

                    {/* Actions — icon buttons, all open modals except the activation-email resend */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {/* Account Status */}
                        <button
                          onClick={() => setStatusModal(user)}
                          title="Update Account Status"
                          className="p-1.5 rounded-lg hover:bg-indigo-50 text-indigo-400 hover:text-indigo-600 transition-colors"
                        >
                          <ShieldCheck size={15} />
                        </button>

                        {/* Payment */}
                        <button
                          onClick={() => setPaymentModal(user)}
                          title="Update Payment Status"
                          className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-400 hover:text-emerald-600 transition-colors"
                        >
                          <CreditCard size={15} />
                        </button>

                        {/* User Count */}
                        <button
                          onClick={() => setUserCountModal(user)}
                          title="Update User Count"
                          className="p-1.5 rounded-lg hover:bg-purple-50 text-purple-400 hover:text-purple-600 transition-colors"
                        >
                          <Hash size={15} />
                        </button>

                        {/* Resend activation email — ACTIVATED accounts only */}
                        {user.actionStatus?.toUpperCase() === 'ACTIVATED' && (
                          <button
                            onClick={() => handleResendActivationEmail(user.id)}
                            title="Resend activation email"
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-400 hover:text-blue-600 transition-colors"
                          >
                            <RefreshCw size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredUsers.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-50 bg-gray-50/50 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-400">
              Showing {filteredUsers.length} of {users.length} users
            </span>
            {/* <div className="flex flex-wrap items-center gap-1.5">
              {ACTION_STATUSES.map(({ value, badgeCls, label }) => (
                <span key={value} className={`px-2 py-0.5 rounded-full text-xs font-medium ${badgeCls}`}>
                  {label}
                </span>
              ))}
            </div> */}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {statusModal && (
        <UpdateStatusModal
          user={statusModal}
          onClose={() => setStatusModal(null)}
          onSave={(status) => handleUpdateStatus(statusModal.id, status)}
        />
      )}

      {paymentModal && (
        <UpdatePaymentModal
          user={paymentModal}
          onClose={() => setPaymentModal(null)}
          onSave={(status) => handleUpdatePayment(paymentModal.id, status)}
        />
      )}

      {userCountModal && (
        <UpdateUserCountModal
          user={userCountModal}
          onClose={() => setUserCountModal(null)}
          onSave={(count) => handleUpdateUserCount(userCountModal.id, count)}
        />
      )}
    </div>
  );
};

export default SuperAdmin;


